import React, { useCallback, useEffect, useState } from 'react';
import { Check, ChevronDown, ChevronRight, Loader2, Plus, RotateCcw, Sparkles, Trash2, X } from 'lucide-react';
import { apiService } from '../../services/api';
import { NewPointVisibility, PointVisibility, defaultVisibility } from './PointVisibility';
import AttachMeeting from '../Points/AttachMeeting';

// Points à aborder avec le client, partagés par projet (et ceux du client qui ne visent aucun projet) ou par client.
// Muse peut en proposer d'après les réunions précédentes ; on coche un point quand il a été abordé.
// external : la réunion a des invités extérieurs à Innowide (points publics par défaut), false : réunion interne
// (privés par défaut) ; non précisé (page projet) : publics.
const TalkingPointsPanel = ({ projectId, clientId, seriesId, meetingTitle, meetingRef, onChange, emptyHint, external }) => {
  const [visibility, setVisibility] = useState(() => defaultVisibility(external));
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState(null); // { id, text }
  const [showDone, setShowDone] = useState(false);
  const [asking, setAsking] = useState(false);
  const [instructions, setInstructions] = useState('');
  const [showInstructions, setShowInstructions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  // Avec une réunion récurrente (seriesId), ses propres points s'ajoutent à ceux du projet ou du client.
  const series = seriesId ? `series_id=${encodeURIComponent(seriesId)}` : null;
  const target = [projectId ? `project_id=${projectId}` : clientId ? `client_id=${clientId}` : null, series]
    .filter(Boolean).join('&') || null;
  const canSuggest = Boolean(projectId || clientId);

  const load = useCallback(async () => {
    if (!target) {
      setPoints([]);
      setLoading(false);
      return;
    }
    try {
      setPoints(await apiService.get(`/talking-points?${target}&status=open&status=discussed`));
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [target]);

  useEffect(() => {
    setLoading(true);
    setSuggestions([]);
    load();
  }, [load]);

  const changed = (next) => {
    setPoints(next);
    onChange?.(next.filter((p) => p.status === 'open').length);
  };

  const add = async (text, details = null, source = 'manual') => {
    const clean = text.trim();
    if (!clean) return null;
    const body = { text: clean, details, source, visibility,
      ...(projectId ? { project_id: projectId } : clientId ? { client_id: clientId } : {}),
      ...(seriesId ? { series_id: seriesId } : {}) };
    try {
      const created = await apiService.post('/talking-points', body);
      changed([...points, created]);
      return created;
    } catch (err) {
      setError(err.message);
      return null;
    }
  };

  const update = async (point, fields) => {
    try {
      const saved = await apiService.patch(`/talking-points/${point.id}`, fields);
      changed(points.map((p) => (p.id === point.id ? saved : p)));
    } catch (err) {
      setError(err.message);
    }
  };

  const remove = async (point) => {
    try {
      await apiService.delete(`/talking-points/${point.id}`);
      changed(points.filter((p) => p.id !== point.id));
    } catch (err) {
      setError(err.message);
    }
  };

  const suggest = async () => {
    setAsking(true);
    setError(null);
    try {
      const result = await apiService.post('/talking-points/suggest', {
        ...(projectId ? { project_id: projectId } : { client_id: clientId }),
        meeting_title: meetingTitle || null,
        instructions: instructions.trim() || null,
      });
      setSuggestions(result.suggestions || []);
      if (!(result.suggestions || []).length) setError('Nexus n\'a rien trouvé de plus à proposer.');
    } catch (err) {
      setError(err.message);
    } finally {
      setAsking(false);
    }
  };

  const keep = async (suggestion) => {
    if (await add(suggestion.text, suggestion.details, 'muse')) {
      setSuggestions((prev) => prev.filter((s) => s !== suggestion));
    }
  };

  const keepAll = async () => {
    for (const suggestion of suggestions) {
      // eslint-disable-next-line no-await-in-loop
      await keep(suggestion);
    }
  };

  if (!target) return <p className="text-sm text-slate-400">{emptyHint || 'Choisis un projet ou un client.'}</p>;

  const open = points.filter((p) => p.status === 'open');
  const done = points.filter((p) => p.status === 'discussed');

  return (
    <div className="space-y-3">
      {loading ? (
        <p className="flex items-center gap-2 text-sm text-slate-400"><Loader2 size={14} className="animate-spin" /> Chargement…</p>
      ) : open.length === 0 ? (
        <p className="text-sm text-slate-400">Aucun point en attente pour le moment.</p>
      ) : (
        <ul className="space-y-1.5">
          {open.map((point) => (
            <li key={point.id} className="group flex items-start gap-2.5 rounded-lg px-1.5 py-1 hover:bg-slate-50">
              <button
                type="button"
                title="Marquer comme abordé"
                onClick={() => update(point, { status: 'discussed', discussed_ref: meetingRef || null })}
                className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-slate-300 text-transparent hover:border-emerald-500 hover:text-emerald-500"
              >
                <Check size={11} />
              </button>
              <div className="min-w-0 flex-1">
                {editing?.id === point.id ? (
                  <input
                    autoFocus
                    value={editing.text}
                    onChange={(event) => setEditing({ ...editing, text: event.target.value })}
                    onBlur={() => {
                      if (editing.text.trim() && editing.text.trim() !== point.text) update(point, { text: editing.text.trim() });
                      setEditing(null);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') event.currentTarget.blur();
                      if (event.key === 'Escape') setEditing(null);
                    }}
                    className="w-full rounded-md border border-slate-200 px-2 py-1 text-sm outline-none focus:ring-2 focus:ring-indigo-100"
                  />
                ) : (
                  <button type="button" onClick={() => setEditing({ id: point.id, text: point.text })} className="text-left text-sm text-slate-800">
                    {point.text}
                  </button>
                )}
                {point.details && <p className="text-xs text-slate-500">{point.details}</p>}
                <p className="text-[11px] text-slate-400">
                  {point.source === 'muse' ? 'Proposé par Nexus' : point.created_by_name || ''}
                  {!projectId && point.project_name ? ` · ${point.project_name}` : ''}
                  {projectId && !point.project_id ? ' · tout le client' : ''}
                </p>
              </div>
              <AttachMeeting point={point} compact={!point.event_id}
                onChange={(saved) => changed(points.map((p) => (p.id === saved.id ? saved : p)))} />
              <PointVisibility point={point} mine={point.mine} onChange={(value) => update(point, { visibility: value })} />
              <button type="button" title="Supprimer" onClick={() => remove(point)} className="p-1 text-slate-300 opacity-0 hover:text-rose-500 group-hover:opacity-100">
                <Trash2 size={13} />
              </button>
            </li>
          ))}
        </ul>
      )}

      <form
        onSubmit={async (event) => {
          event.preventDefault();
          if (await add(draft)) setDraft('');
        }}
        className="flex items-center gap-2"
      >
        <input
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder="Ajouter un point à aborder…"
          className="min-w-0 flex-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-indigo-100"
        />
        <NewPointVisibility value={visibility} onChange={setVisibility} />
        <button type="submit" disabled={!draft.trim()} className="rounded-lg bg-slate-900 p-2 text-white hover:bg-slate-700 disabled:opacity-40" title="Ajouter">
          <Plus size={14} />
        </button>
      </form>

      {canSuggest && (
      <div className="rounded-xl border border-indigo-100 bg-indigo-50/40 p-3">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={suggest}
            disabled={asking}
            className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
          >
            {asking ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
            {asking ? 'Nexus réfléchit…' : 'Proposer avec Nexus'}
          </button>
          <button type="button" onClick={() => setShowInstructions((v) => !v)} className="text-xs text-indigo-700 hover:underline">
            {showInstructions ? 'Masquer la consigne' : 'Préciser ce que tu veux aborder'}
          </button>
        </div>
        {showInstructions && (
          <textarea
            value={instructions}
            onChange={(event) => setInstructions(event.target.value)}
            rows={2}
            placeholder="Ex. : insiste sur le planning des essais et le budget."
            className="mt-2 w-full rounded-lg border border-indigo-100 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-100"
          />
        )}
        {suggestions.length > 0 && (
          <div className="mt-3 space-y-1.5">
            <div className="flex items-center justify-between">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-indigo-700">Propositions de Nexus</p>
              <button type="button" onClick={keepAll} className="text-xs font-semibold text-indigo-700 hover:underline">Tout ajouter</button>
            </div>
            {suggestions.map((suggestion) => (
              <div key={suggestion.text} className="flex items-start gap-2 rounded-lg bg-white px-2.5 py-2 shadow-sm animate-muse-line-in">
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-slate-800">{suggestion.text}</p>
                  {suggestion.details && <p className="text-xs text-slate-500">{suggestion.details}</p>}
                </div>
                <button type="button" onClick={() => keep(suggestion)} className="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold text-indigo-700 hover:bg-indigo-50">
                  <Plus size={12} /> Ajouter
                </button>
                <button type="button" title="Écarter" onClick={() => setSuggestions((prev) => prev.filter((s) => s !== suggestion))} className="p-1 text-slate-300 hover:text-slate-500">
                  <X size={13} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      {error && <p className="text-xs text-rose-600">{error}</p>}

      {done.length > 0 && (
        <div>
          <button type="button" onClick={() => setShowDone((v) => !v)} className="flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-700">
            {showDone ? <ChevronDown size={13} /> : <ChevronRight size={13} />} Déjà abordés ({done.length})
          </button>
          {showDone && (
            <ul className="mt-1.5 space-y-1">
              {done.map((point) => (
                <li key={point.id} className="flex items-start gap-2 px-1.5 text-sm text-slate-400">
                  <Check size={13} className="mt-0.5 shrink-0 text-emerald-500" />
                  <span className="flex-1 line-through decoration-slate-300">{point.text}</span>
                  <button type="button" title="Remettre à aborder" onClick={() => update(point, { status: 'open' })} className="p-0.5 hover:text-slate-600">
                    <RotateCcw size={12} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default TalkingPointsPanel;
